import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { chevronBackOutline, chevronForwardOutline, closeOutline } from 'ionicons/icons';

export interface TicketImageViewerItem {
  id: string;
  src: string;
  alt: string;
  label: string;
}

@Component({
  selector: 'app-ticket-image-viewer',
  standalone: true,
  imports: [CommonModule, IonIcon],
  templateUrl: './ticket-image-viewer.component.html',
  styleUrls: ['./ticket-image-viewer.component.scss']
})
export class TicketImageViewerComponent implements OnChanges {
  @Input() images: readonly TicketImageViewerItem[] = [];
  @Input() isOpen = false;
  @Input() initialIndex = 0;
  @Output() closed = new EventEmitter<void>();

  activeIndex = 0;
  zoomLevel = 0;
  dragOffset = 0;
  isDragging = false;

  private pointerId: number | null = null;
  private dragStartX = 0;

  constructor() {
    addIcons({ chevronBackOutline, chevronForwardOutline, closeOutline });
  }

  ngOnChanges(): void {
    if (!this.images.length) {
      this.activeIndex = 0;
      this.resetImageInteraction();
      return;
    }

    if (this.isOpen) {
      this.activeIndex = this.clampIndex(this.initialIndex);
      this.resetImageInteraction();
      return;
    }

    this.activeIndex = this.clampIndex(this.activeIndex);
  }

  get currentImage(): TicketImageViewerItem | null {
    return this.images[this.activeIndex] ?? null;
  }

  get hasMultipleImages(): boolean {
    return this.images.length > 1;
  }

  get zoomScale(): number {
    return [1, 1.6, 2.25][this.zoomLevel] ?? 1;
  }

  get imageTransform(): string {
    return `translateX(${this.dragOffset}px) scale(${this.zoomScale})`;
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showPrevious();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showNext();
    }
  }

  close(): void {
    this.resetImageInteraction();
    this.closed.emit();
  }

  showPrevious(): void {
    if (!this.hasMultipleImages) {
      return;
    }
    this.activeIndex = (this.activeIndex - 1 + this.images.length) % this.images.length;
    this.resetImageInteraction();
  }

  showNext(): void {
    if (!this.hasMultipleImages) {
      return;
    }
    this.activeIndex = (this.activeIndex + 1) % this.images.length;
    this.resetImageInteraction();
  }

  showImage(index: number): void {
    if (index === this.activeIndex) {
      return;
    }
    this.activeIndex = this.clampIndex(index);
    this.resetImageInteraction();
  }

  advanceZoom(): void {
    this.zoomLevel = (this.zoomLevel + 1) % 3;
  }

  startDrag(event: PointerEvent): void {
    if (!this.hasMultipleImages || event.button !== 0) {
      return;
    }

    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragOffset = 0;
    this.isDragging = true;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  moveDrag(event: PointerEvent): void {
    if (!this.isDragging || event.pointerId !== this.pointerId) {
      return;
    }
    this.dragOffset = Math.max(-140, Math.min(140, event.clientX - this.dragStartX));
  }

  endDrag(event: PointerEvent): void {
    if (!this.isDragging || event.pointerId !== this.pointerId) {
      return;
    }

    const offset = this.dragOffset;
    this.resetImageInteraction();
    if (offset <= -56) {
      this.showNext();
    } else if (offset >= 56) {
      this.showPrevious();
    }
  }

  private clampIndex(index: number): number {
    return Math.max(0, Math.min(index, this.images.length - 1));
  }

  private resetImageInteraction(): void {
    this.zoomLevel = 0;
    this.dragOffset = 0;
    this.isDragging = false;
    this.pointerId = null;
  }
}
